import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';

const PAGE_SIZE = 50;

const SERVER_CONFIG = {
  MENA: { color: '#f97316' },
  FR:   { color: '#3b82f6' },
  IT:   { color: '#22c55e' },
  DE:   { color: '#a855f7' },
};

const LEVEL_CONFIG = {
  Junior: { color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
  Rising: { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
  Senior: { color: '#FF6B00', bg: 'rgba(255,107,0,0.15)' },
  Elite:  { color: '#FFD700', bg: 'rgba(255,215,0,0.15)' },
};

const ServerBadge = ({ server }) => {
  const cfg = SERVER_CONFIG?.[server];
  if (!cfg) return <span className="text-xs text-white/30">—</span>;
  return (
    <span
      className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md"
      style={{ backgroundColor: `${cfg?.color}22`, color: cfg?.color, fontFamily: 'Sora, sans-serif' }}
    >
      {server}
    </span>
  );
};

const LevelBadge = ({ level }) => {
  const cfg = LEVEL_CONFIG?.[level];
  if (!cfg) return <span className="text-xs text-white/30">—</span>;
  return (
    <span
      className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md"
      style={{ backgroundColor: cfg?.bg, color: cfg?.color, fontFamily: 'Inter, sans-serif' }}
    >
      {level}
    </span>
  );
};

const ManagerCreatorsOverview = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [serverFilter, setServerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [whatsappFilter, setWhatsappFilter] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('in_agency');

  // Table state
  const [creators, setCreators] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('username');
  const [sortDir, setSortDir] = useState('asc');

  const searchDebounceRef = useRef(null);

  // Auth init
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filterParam = params?.get('filter');
    if (filterParam === 'inactive') setStatusFilter('inactive_month');
    if (filterParam === 'sans_whatsapp') setWhatsappFilter('without');

    const init = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) { navigate('/login-authentication'); return; }
        const { data: profile } = await supabase?.from('users')?.select('*')?.eq('id', user?.id)?.single();
        if (!profile) { navigate('/login-authentication'); return; }
        setManagerProfile(profile);
        setCurrentUser(user);
        setAuthLoading(false);
      } catch (err) {
        console.error('Init error:', err);
        setAuthLoading(false);
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchCreators = useCallback(async (page, sort, dir, searchVal, server, status, level, tier, waFilter, userId, agFilter) => {
    if (!userId) return;
    setLoading(true);
    try {
      const searchTerm = searchVal?.trim()?.toLowerCase() || '';

      // Fetch creators table for enrichment (whatsapp, display_name, level)
      const { data: creatorsEnrich } = await supabase?.from('creators')
        ?.select('tiktok_id, whatsapp, display_name, level')?.eq('manager_id', userId);
      const enrichMap = {};
      creatorsEnrich?.forEach(r => { enrichMap[r.tiktok_id] = r; });

      if (agFilter === 'in_agency') {
        let query = supabase?.from('creator_current_month')?.select('*')?.eq('manager_id', userId);
        if (server) query = query?.eq('server', server);
        if (status === 'active_month') query = query?.eq('is_active', true);
        else if (status === 'inactive_month') query = query?.eq('is_active', false);
        if (tier) query = query?.eq('tier_current', parseInt(tier));

        const { data, error } = await query;
        if (error) throw error;

        // Enrich with creators data
        let filtered = (data || [])?.map(r => ({
          ...r,
          whatsapp: enrichMap?.[r?.tiktok_id]?.whatsapp || null,
          display_name: enrichMap?.[r?.tiktok_id]?.display_name || null,
          level: enrichMap?.[r?.tiktok_id]?.level || null,
        }));

        // Client-side filters
        if (searchTerm) {
          filtered = filtered?.filter(r =>
            r?.username?.toLowerCase()?.includes(searchTerm) ||
            r?.tiktok_id?.toLowerCase()?.includes(searchTerm) ||
            r?.display_name?.toLowerCase()?.includes(searchTerm) ||
            r?.whatsapp?.toLowerCase()?.includes(searchTerm)
          );
        }
        if (level) filtered = filtered?.filter(r => r?.level === level);
        if (waFilter === 'with') filtered = filtered?.filter(r => r?.whatsapp && r?.whatsapp?.trim() !== '');
        else if (waFilter === 'without') filtered = filtered?.filter(r => !r?.whatsapp || r?.whatsapp?.trim() === '');

        filtered?.sort((a, b) => {
          const aVal = a?.[sort] ?? '';
          const bVal = b?.[sort] ?? '';
          if (typeof aVal === 'number' && typeof bVal === 'number') return dir === 'asc' ? aVal - bVal : bVal - aVal;
          return dir === 'asc' ? String(aVal)?.localeCompare(String(bVal)) : String(bVal)?.localeCompare(String(aVal));
        });
        const from = (page - 1) * PAGE_SIZE;
        setCreators(filtered?.slice(from, from + PAGE_SIZE));
        setTotalCount(filtered?.length);

      } else if (agFilter === 'left_agency') {
        let query = supabase?.from('creators')?.select('tiktok_id, username, display_name, server, level, contract_start, departure_date, agency_status, manager_id')?.eq('agency_status', 'left_agency')?.eq('manager_id', userId);
        if (server) query = query?.eq('server', server);
        if (level) query = query?.eq('level', level);
        const { data, error } = await query;
        if (error) throw error;

        let filtered = data || [];
        if (searchTerm) {
          filtered = filtered?.filter(r =>
            r?.username?.toLowerCase()?.includes(searchTerm) ||
            r?.display_name?.toLowerCase()?.includes(searchTerm) ||
            r?.tiktok_id?.toLowerCase()?.includes(searchTerm)
          );
        }
        filtered?.sort((a, b) => {
          const aVal = a?.[sort] ?? '';
          const bVal = b?.[sort] ?? '';
          if (typeof aVal === 'number' && typeof bVal === 'number') return dir === 'asc' ? aVal - bVal : bVal - aVal;
          return dir === 'asc' ? String(aVal)?.localeCompare(String(bVal)) : String(bVal)?.localeCompare(String(aVal));
        });
        const from = (page - 1) * PAGE_SIZE;
        setCreators(filtered?.slice(from, from + PAGE_SIZE));
        setTotalCount(filtered?.length);

      } else {
        // 'tous' — fetch both and merge
        const [currentRes, leftRes] = await Promise.all([
          supabase?.from('creator_current_month')?.select('*')?.eq('manager_id', userId),
          supabase?.from('creators')?.select('tiktok_id, username, display_name, server, level, contract_start, departure_date, agency_status, manager_id')?.eq('agency_status', 'left_agency')?.eq('manager_id', userId),
        ]);
        // Enrich current month data with creators fields
        const currentData = (currentRes?.data || [])?.map(r => ({
          ...r, _source: 'current',
          whatsapp: enrichMap?.[r?.tiktok_id]?.whatsapp || null,
          display_name: enrichMap?.[r?.tiktok_id]?.display_name || r?.display_name || null,
          level: enrichMap?.[r?.tiktok_id]?.level || r?.level || null,
        }));
        const leftData = (leftRes?.data || [])?.map(r => ({ ...r, _source: 'left', is_active: false }));
        const currentIds = new Set(currentData.map(r => r.tiktok_id));
        const uniqueLeft = leftData?.filter(r => !currentIds?.has(r?.tiktok_id));
        let merged = [...currentData, ...uniqueLeft];

        if (searchTerm) {
          merged = merged?.filter(r =>
            r?.username?.toLowerCase()?.includes(searchTerm) ||
            r?.display_name?.toLowerCase()?.includes(searchTerm) ||
            r?.tiktok_id?.toLowerCase()?.includes(searchTerm) ||
            r?.whatsapp?.toLowerCase()?.includes(searchTerm)
          );
        }
        if (server) merged = merged?.filter(r => r?.server === server);
        if (status === 'active_month') merged = merged?.filter(r => r?.is_active === true);
        else if (status === 'inactive_month') merged = merged?.filter(r => !r?.is_active);
        if (level) merged = merged?.filter(r => r?.level === level);
        if (tier) merged = merged?.filter(r => r?.tier_current === parseInt(tier));
        if (waFilter === 'with') merged = merged?.filter(r => r?.whatsapp && r?.whatsapp?.trim() !== '');
        else if (waFilter === 'without') merged = merged?.filter(r => !r?.whatsapp || r?.whatsapp?.trim() === '');

        merged?.sort((a, b) => {
          const aVal = a?.[sort] ?? '';
          const bVal = b?.[sort] ?? '';
          if (typeof aVal === 'number' && typeof bVal === 'number') return dir === 'asc' ? aVal - bVal : bVal - aVal;
          return dir === 'asc' ? String(aVal)?.localeCompare(String(bVal)) : String(bVal)?.localeCompare(String(aVal));
        });
        const from = (page - 1) * PAGE_SIZE;
        setCreators(merged?.slice(from, from + PAGE_SIZE));
        setTotalCount(merged?.length);
      }
    } catch (err) {
      console.error('Fetch creators error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !currentUser) return;
    fetchCreators(currentPage, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, whatsappFilter, currentUser?.id, agencyFilter);
  }, [authLoading, currentUser]);

  const handleSearch = (val) => {
    setSearch(val);
    setCurrentPage(1);
    clearTimeout(searchDebounceRef?.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchCreators(1, sortField, sortDir, val, serverFilter, statusFilter, levelFilter, tierFilter, whatsappFilter, currentUser?.id, agencyFilter);
    }, 350);
  };

  const handleFilterChange = (updates) => {
    const newState = {
      page: 1, search, server: serverFilter, status: statusFilter,
      level: levelFilter, tier: tierFilter, wa: whatsappFilter, agency: agencyFilter,
      ...updates,
    };
    setCurrentPage(newState?.page);
    if (updates?.server !== undefined) setServerFilter(updates?.server);
    if (updates?.status !== undefined) setStatusFilter(updates?.status);
    if (updates?.level !== undefined) setLevelFilter(updates?.level);
    if (updates?.tier !== undefined) setTierFilter(updates?.tier);
    if (updates?.wa !== undefined) setWhatsappFilter(updates?.wa);
    if (updates?.agency !== undefined) setAgencyFilter(updates?.agency);
    fetchCreators(1, sortField, sortDir, newState?.search, newState?.server, newState?.status, newState?.level, newState?.tier, newState?.wa, currentUser?.id, newState?.agency);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      const newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
      fetchCreators(currentPage, field, newDir, search, serverFilter, statusFilter, levelFilter, tierFilter, whatsappFilter, currentUser?.id, agencyFilter);
    } else {
      setSortField(field);
      setSortDir('asc');
      fetchCreators(currentPage, field, 'asc', search, serverFilter, statusFilter, levelFilter, tierFilter, whatsappFilter, currentUser?.id, agencyFilter);
    }
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-white/20 ml-1">↕</span>;
    return <span className="ml-1" style={{ color: '#E5202A' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const getContractStyle = (contractEnd) => {
    if (!contractEnd) return {};
    const daysLeft = Math.ceil((new Date(contractEnd) - new Date()) / 86400000);
    if (daysLeft <= 30 && daysLeft > 0) return { color: '#FFD700' };
    if (daysLeft <= 0) return { color: '#ef4444' };
    return {};
  };

  const isLeftAgency = (row) => row?.agency_status === 'left_agency' || row?._source === 'left';

  if (authLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <ManagerSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
        managerName={managerProfile?.name}
      />
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <ManagerMobileHeader title={t('creators.title')} />
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <h1 className="font-bold text-white mb-1" style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(18px, 4vw, 24px)' }}>{t('creators.title')}</h1>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>
              {t('creators.found', '{{count}} creator(s) found', { count: totalCount })}
            </p>
          </div>

          {/* Filters */}
          <div className="mb-4 md:mb-6 space-y-3">
            {/* Agency status filter tabs */}
            <div className="flex items-center gap-1 rounded-lg p-1 w-fit" style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}>
              {[
                { value: 'in_agency', label: t('common.inAgency') },
                { value: 'left_agency', label: t('common.left') },
                { value: 'tous', label: t('common.all') },
              ]?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => handleFilterChange({ agency: opt?.value })}
                  className="px-3 py-1.5 rounded text-xs font-semibold transition-colors"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    backgroundColor: agencyFilter === opt?.value
                      ? opt?.value === 'left_agency' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)'
                      : 'transparent',
                    color: agencyFilter === opt?.value
                      ? opt?.value === 'left_agency' ? '#ef4444' : 'rgba(255,255,255,0.9)'
                      : 'rgba(255,255,255,0.4)',
                    minHeight: '36px',
                  }}
                >
                  {opt?.label}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder={t('creators.search')}
              value={search}
              onChange={e => handleSearch(e?.target?.value)}
              className="w-full px-3 py-2.5 rounded-lg text-base text-white placeholder-white/30 border outline-none focus:border-red-500/50 transition-colors"
              style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', fontSize: '16px' }}
            />

            {/* Scrollable filter pills */}
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {[
                { label: t('creators.server'), value: serverFilter, key: 'server', options: [{ v: '', l: t('common.allServers') }, { v: 'MENA', l: 'MENA' }, { v: 'FR', l: 'FR' }, { v: 'IT', l: 'IT' }, { v: 'DE', l: 'DE' }] },
                { label: t('creators.status'), value: statusFilter, key: 'status', options: [{ v: '', l: t('common.allStatus') }, { v: 'active_month', l: t('creators.activeMonth') }, { v: 'inactive_month', l: t('creators.inactiveMonth') }] },
                { label: t('creators.level'), value: levelFilter, key: 'level', options: [{ v: '', l: t('common.allLevels') }, { v: 'Junior', l: 'Junior' }, { v: 'Rising', l: 'Rising' }, { v: 'Senior', l: 'Senior' }, { v: 'Elite', l: 'Elite' }] },
                { label: t('creators.tier'), value: tierFilter, key: 'tier', options: [{ v: '', l: t('common.allTiers') }, ...Array.from({ length: 10 }, (_, i) => ({ v: String(i + 1), l: `T${i + 1}` }))] },
              ]?.map(f => (
                <select
                  key={f?.label}
                  value={f?.value}
                  onChange={e => handleFilterChange({ [f?.key]: e?.target?.value })}
                  className="flex-shrink-0 px-3 py-2 rounded-lg text-base text-white border outline-none focus:border-red-500/50 transition-colors"
                  style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', fontSize: '16px', minHeight: '44px' }}
                >
                  {f?.options?.map(o => <option key={o?.v} value={o?.v}>{o?.l}</option>)}
                </select>
              ))}
              {/* WhatsApp filter */}
              <div className="flex-shrink-0 flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', minHeight: '44px' }}>
                {[
                  { value: '', label: t('common.all') },
                  { value: 'with', label: t('common.with') },
                  { value: 'without', label: t('common.without') },
                ]?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => handleFilterChange({ wa: opt?.value })}
                    className="px-2.5 py-1 rounded text-xs font-semibold transition-colors"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      backgroundColor: whatsappFilter === opt?.value
                        ? opt?.value === 'with' ? 'rgba(37,211,102,0.2)' : opt?.value === 'without' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)'
                        : 'transparent',
                      color: whatsappFilter === opt?.value
                        ? opt?.value === 'with' ? '#25D366' : opt?.value === 'without' ? '#ef4444' : 'rgba(255,255,255,0.8)'
                        : 'rgba(255,255,255,0.4)',
                    }}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
              {(search || serverFilter || statusFilter || levelFilter || tierFilter || whatsappFilter) && (
                <button
                  onClick={() => {
                    setSearch(''); setServerFilter(''); setStatusFilter('');
                    setLevelFilter(''); setTierFilter(''); setWhatsappFilter('');
                    setCurrentPage(1);
                    fetchCreators(1, sortField, sortDir, '', '', '', '', '', '', currentUser?.id, agencyFilter);
                  }}
                  className="flex-shrink-0 px-3 py-2 rounded-lg text-sm text-white/50 hover:text-white border transition-colors"
                  style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px' }}
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>

          {/* Mobile: Card list */}
          <div className="md:hidden space-y-3">
            {loading ? (
              Array.from({ length: 6 })?.map((_, i) => (
                <div key={i} className="rounded-xl border animate-pulse" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', height: '90px' }} />
              ))
            ) : creators?.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                {t('creators.noCreatorFound', 'No creator found')}
              </div>
            ) : creators?.map(creator => {
              const active = creator?.is_active;
              const tierCurrent = creator?.tier_current;
              const lastLiveDate = creator?.last_live_date || null;
              const leftAgency = isLeftAgency(creator);

              return (
                <div
                  key={creator?.tiktok_id}
                  onClick={() => navigate(`/manager/creators/${creator?.tiktok_id}`)}
                  className="rounded-xl border cursor-pointer transition-colors"
                  style={{
                    backgroundColor: leftAgency ? 'rgba(107,114,128,0.06)' : '#111111',
                    borderColor: leftAgency ? 'rgba(107,114,128,0.2)' : '#1f1f1f',
                    padding: '12px 14px',
                    opacity: leftAgency ? 0.75 : 1,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-bold truncate" style={{ fontFamily: 'Inter, sans-serif', color: leftAgency ? 'rgba(255,255,255,0.5)' : '#fff' }}>
                        {creator?.username || creator?.tiktok_id}
                      </span>
                      {creator?.is_new_recruit === true && (
                        <span className="inline-flex items-center text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', fontFamily: 'Sora, sans-serif' }}>NEW</span>
                      )}
                      <ServerBadge server={creator?.server} />
                    </div>
                    {leftAgency ? (
                      <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md flex-shrink-0" style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>{t('creators.left')}</span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md flex-shrink-0" style={{ backgroundColor: active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: active ? '#22c55e' : '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                        {active ? '🟢 ' + t('creators.activeMonth', 'Active this month') : '🔴 ' + t('creators.inactiveMonth', 'Inactive this month')}
                      </span>
                    )}
                  </div>
                  {creator?.display_name && (
                    <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{creator?.display_name}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <LevelBadge level={creator?.level} />
                    {tierCurrent && (
                      <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>T{tierCurrent}</span>
                    )}
                    {lastLiveDate ? (
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                        {t('creators.lastLive')}: {new Date(lastLiveDate)?.toLocaleDateString({ fr: 'fr-FR', en: 'en-US', it: 'it-IT', de: 'de-DE' }[i18n.language] || 'en-US')}
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>{t('creators.neverLive')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Table */}
          <div className="hidden md:block rounded-xl border overflow-hidden" style={{ borderColor: '#1f1f1f' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: '#0d0d0d', borderBottom: '1px solid #1f1f1f' }}>
                    {[
                      { label: t('creators.creator'), field: 'username' },
                      { label: 'Display Name', field: 'display_name' },
                      { label: 'TikTok ID', field: 'tiktok_id' },
                      { label: t('creators.server'), field: 'server' },
                      { label: t('creators.tier'), field: 'tier_current' },
                      { label: t('creators.level'), field: 'level' },
                      { label: t('creators.whatsapp'), field: null },
                      { label: t('creators.status'), field: null },
                      { label: t('creators.lastLive'), field: 'last_live_date' },
                      { label: t('creators.contract'), field: 'contract_end' },
                    ]?.map(col => (
                      <th
                        key={col?.label}
                        className={`px-4 py-3 text-left text-xs font-bold text-white/40 uppercase tracking-wider ${col?.field ? 'cursor-pointer hover:text-white/70' : ''}`}
                        style={{ fontFamily: 'Sora, sans-serif' }}
                        onClick={() => col?.field && handleSort(col?.field)}
                      >
                        {col?.label}{col?.field && <SortIcon field={col?.field} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 })?.map((_, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1f1f1f' }}>
                        {Array.from({ length: 10 })?.map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: j === 0 ? '120px' : '80px' }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : creators?.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {t('creators.noCreatorFound', 'No creator found')}
                      </td>
                    </tr>
                  ) : creators?.map(creator => {
                    const active = creator?.is_active;
                    const tierCurrent = creator?.tier_current;
                    const contractStyle = getContractStyle(creator?.contract_end);
                    const lastLiveDate = creator?.last_live_date || null;
                    const whatsapp = creator?.whatsapp || null;
                    const leftAgency = isLeftAgency(creator);

                    return (
                      <tr
                        key={creator?.tiktok_id}
                        onClick={() => navigate(`/manager/creators/${creator?.tiktok_id}`)}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid #1f1f1f',
                          backgroundColor: leftAgency ? 'rgba(107,114,128,0.04)' : 'transparent',
                          opacity: leftAgency ? 0.7 : 1,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#161616'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = leftAgency ? 'rgba(107,114,128,0.04)' : 'transparent'; }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif', color: leftAgency ? 'rgba(255,255,255,0.45)' : '#fff' }}>
                              {creator?.username || '—'}
                            </span>
                            {creator?.is_new_recruit === true && (
                              <span className="inline-flex items-center text-xs font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', fontFamily: 'Sora, sans-serif' }}>NEW</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                            {creator?.display_name || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-white/40 font-mono">{creator?.tiktok_id}</span>
                        </td>
                        <td className="px-4 py-3"><ServerBadge server={creator?.server} /></td>
                        <td className="px-4 py-3">
                          {tierCurrent ? (
                            <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>T{tierCurrent}</span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>—</span>
                          )}
                        </td>
                        <td className="px-4 py-3"><LevelBadge level={creator?.level} /></td>
                        <td className="px-4 py-3" onClick={e => e?.stopPropagation()}>
                          {whatsapp ? (
                            <a
                              href={`https://wa.me/${whatsapp?.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold transition-colors hover:opacity-80"
                              style={{ backgroundColor: 'rgba(37,211,102,0.12)', color: '#25D366', border: '1px solid rgba(37,211,102,0.25)', textDecoration: 'none', fontFamily: 'Inter, sans-serif' }}
                            >
                              {whatsapp}
                            </a>
                          ) : (
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {leftAgency ? (
                            <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
                              {t('creators.left')} {creator?.departure_date ? `· ${creator?.departure_date}` : ''}
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: active ? '#22c55e' : '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                              {active ? '🟢 ' + t('creators.activeMonth', 'Active this month') : '🔴 ' + t('creators.inactiveMonth', 'Inactive this month')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {lastLiveDate ? (
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif' }}>
                              {new Date(lastLiveDate)?.toLocaleDateString('fr-FR')}
                            </span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>{t('creators.neverLive')}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs" style={{ ...contractStyle, fontFamily: 'Inter, sans-serif', color: contractStyle?.color || 'rgba(255,255,255,0.4)' }}>
                            {creator?.contract_end || '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>
                Page {currentPage} / {totalPages} — {t('creators.found', '{{count}} creator(s) found', { count: totalCount })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, currentPage - 1);
                    setCurrentPage(newPage);
                    fetchCreators(newPage, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, whatsappFilter, currentUser?.id, agencyFilter);
                  }}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded-lg text-xs text-white/50 border transition-colors disabled:opacity-30"
                  style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px', minWidth: '44px' }}
                >←</button>
                <span className="text-xs text-white/40" style={{ fontFamily: 'Inter, sans-serif' }}>{currentPage}/{totalPages}</span>
                <button
                  onClick={() => {
                    const newPage = Math.min(totalPages, currentPage + 1);
                    setCurrentPage(newPage);
                    fetchCreators(newPage, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, whatsappFilter, currentUser?.id, agencyFilter);
                  }}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded-lg text-xs text-white/50 border transition-colors disabled:opacity-30"
                  style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px', minWidth: '44px' }}
                >→</button>
              </div>
            </div>
          )}
        </div>
      </main>
      <ManagerBottomNav />
    </div>
  );
};

export default ManagerCreatorsOverview;
